import { Mutation, Action } from 'vuex-module-decorators';
import { Resolve } from 'vue-di';
import { InjectModule, InjectVuexModule } from 'vue-di/vuex';
import { EdgePatientEnrollmentApi } from '@trialcard/apigateway.client/edgePatientEnrollmentApi';
import {
    EnrollPatientRequestModel,
    EnterpriseCoordinatorAccountUpsearchRequestModel,
    UpsearchModelEnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
    UpsearchModelEnterpriseCoordinatorAccountServiceModelsAccountUpsearchModel,
    EnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
    EnterpriseCoordinatorSharedAddressUpsearchModel,
    EnterpriseCoordinatorSharedEmailAddressUpsearchModel,
    EnterpriseCoordinatorSharedPhoneUpsearchModel,
    EnterpriseCoordinatorAccountServiceModelsAccountContactUpsearchModel,
    EnterpriseCoordinatorAccountServiceModelsAccountContactPreferenceModel,
    PrescriberModel,
    SiteModel,
    EnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel,
    EnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel,
    UpsearchModelEnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel,
    EnterpriseCoordinatorPayorServiceModelsPayorBenefitModel,
    EnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel,
    UpsearchModelEnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel,
} from '@trialcard/apigateway.models';
import * as enums from '@trialcard/enums';
import { Moment } from 'moment';
import { AxiosResponse } from 'axios';
import { EdgeEnrollmentApi, EdgeDocumentsApi, EnrollmentV1Api } from '@trialcard/apigateway.client';
import _ from 'lodash';
import SessionModule from './session';
import { PatientAccountDisplayDetails } from './interfaces';
import { OnClear } from '~/services/clearModule';
import { Settings } from '~/services/settings';

import SurveyModule from '~/store/survey';
import { clearRecords } from '~/services/clearRecords';
import CommonTools from '~/components/tools/commonTools';
import { ReenrollPages } from '~/types/pageTypes';

@InjectModule({ stateFactory: true }, module)
@OnClear<EnrollmentModule>(async value => value.clear())
export default class EnrollmentModule extends InjectVuexModule {
    @Resolve
    patientEnrollmentApi!: EdgePatientEnrollmentApi;
    @Resolve
    enrollmentApi!: EdgeEnrollmentApi;
    @Resolve
    enrollmentServiceApi!: EnrollmentV1Api;
    @Resolve
    documentApi!: EdgeDocumentsApi;
    @Resolve
    settings!: Settings;
    @Resolve
    survey!: SurveyModule;
    @Resolve
    session!: SessionModule;

    alternativeEnrollment: boolean | null = null;
    medicalMemberNumber: string | null = null;
    pharmacyMemberNumber: string | null = null;
    expirationDate: Moment | null = null;
    selectedPrescriber = {} as EnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel | null;
    selectedPractice = {} as EnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel | null;
    reenrollPatient!: PatientAccountDisplayDetails;

    isReenrollment: boolean | null = null;
    reenrollmentPages: ReenrollPages[] = [];

    paymentType = '';

    defaultPatientInformationData = {
        firstName: '' as string,
        lastName: '' as string,
        dateOfBirth: null as Moment | null,
        addresses: [] as EnterpriseCoordinatorSharedAddressUpsearchModel[],
        emailAddresses: [] as EnterpriseCoordinatorSharedEmailAddressUpsearchModel[],
        phoneNumbers: [] as EnterpriseCoordinatorSharedPhoneUpsearchModel[],
        gender: null as enums.Gender | null,
    };

    primaryPayerInformationData = {
        name: '' as string,
        groupNumber: '' as string,
        phone: '' as string,
    };

    secondaryPayerInformationData = {
        name: '' as string,
        groupNumber: '' as string | null,
        phone: '' as string | null,
    };

    primaryPrescriptionInformationData = {
        name: '' as string,
        bin: '' as string,
        pcn: '' as string | null,
        phone: '' as string | null,
        groupNumber: '' as string | null,
        policyHolderId: '' as string | null,
    };

    secondaryPrescriptionInformationData = {
        name: '' as string,
        bin: '' as string | null,
        pcn: '' as string | null,
        phone: '' as string | null,
        groupNumber: '' as string | null,
        policyHolderId: '' as string | null,
    };

    patientInformationData = { ...this.defaultPatientInformationData };
    contactPreferences = [] as EnterpriseCoordinatorAccountServiceModelsAccountContactPreferenceModel[];
    thirdPartyId = '';
    isReenrollPatientUpdated(currentPatient: PatientAccountDisplayDetails) {
        const patientAddress = (this.patientInformationData.addresses || [])[0] || {};
        const enrollAddress = (currentPatient.addresses || [])[0] || {};
        const patientEmail = (this.patientInformationData.emailAddresses || [])[0] || {};
        const enrollEmail = (currentPatient.emailAddresses || [])[0] || {};
        const patientphone = (this.patientInformationData.phoneNumbers || [])[0] || {};
        const enrollPhone = (currentPatient.phoneNumbers || [])[0] || {};

        const isEqual =
            patientAddress.addressOne === enrollAddress.addressOne &&
            patientAddress.city === enrollAddress.city &&
            patientAddress.zip === enrollAddress.zip &&
            patientAddress.state === enrollAddress.state &&
            (patientEmail.address || '') === (enrollEmail.address || '') &&
            patientphone.phoneNumber === enrollPhone.phoneNumber;

        return !isEqual;
    }

    @Mutation
    setReenrollPatient(patient: PatientAccountDisplayDetails) {
        this.reenrollPatient = patient;
    }

    @Mutation
    setThirdPartyId(id: string) {
        this.thirdPartyId = id;
    }

    @Mutation
    setAllReenrollPages() {
        this.reenrollmentPages.push(ReenrollPages.PatientInformation, ReenrollPages.PayerInformation, ReenrollPages.PrescriberInformation);
    }

    @Mutation
    removeEnrollmentPage(page: ReenrollPages) {
        const index = this.reenrollmentPages.findIndex(item => item === page);
        if (index >= 0) {
            this.reenrollmentPages.splice(index, 1);
        }
    }

    @Mutation
    async updatePrescriber(prescriber: PrescriberModel) {
        this.selectedPrescriber = {
            ...prescriber,
        } as EnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel;
    }

    @Mutation
    async updatePractice(site: SiteModel) {
        this.selectedPractice = ({
            ...site,
        } as unknown) as EnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel;
    }

    @Mutation
    async updatePaymentType(type: string) {
        this.paymentType = type;
    }

    @Mutation
    async updatePatientInformation(newPatientInformation: IPatientInformation) {
        this.patientInformationData = { ...this.patientInformationData, ...newPatientInformation };
    }

    @Mutation
    async updatePrimaryPayerInformation(newPayerInformation: typeof EnrollmentModule.prototype.primaryPayerInformationData) {
        Object.assign(this.primaryPayerInformationData, newPayerInformation);
    }

    @Mutation
    async updatePrimaryPrescriptionInformation(newPayerInformation: typeof EnrollmentModule.prototype.primaryPrescriptionInformationData) {
        Object.assign(this.primaryPrescriptionInformationData, newPayerInformation);
    }

    @Mutation
    async updateSecondaryPayerInformation(newPayerInformation: typeof EnrollmentModule.prototype.secondaryPayerInformationData) {
        Object.assign(this.secondaryPayerInformationData, newPayerInformation);
    }

    @Mutation
    async updateSecondaryPrescriptionInformation(
        newPayerInformation: typeof EnrollmentModule.prototype.secondaryPrescriptionInformationData
    ) {
        Object.assign(this.secondaryPrescriptionInformationData, newPayerInformation);
    }

    @Mutation
    setReenrollment(value: boolean) {
        this.isReenrollment = value;
    }

    getSelectedPractice() {
        return this.selectedPractice;
    }

    getSelectedPrescriber() {
        return this.selectedPrescriber;
    }

    @Mutation
    clear() {
        clearRecords(this.defaultPatientInformationData);
        clearRecords(this.patientInformationData);
        clearRecords(this.primaryPayerInformationData);
        clearRecords(this.primaryPrescriptionInformationData);
        clearRecords(this.secondaryPayerInformationData);
        clearRecords(this.secondaryPrescriptionInformationData);
        clearRecords(this.contactPreferences);
        clearRecords(this.paymentType);
        clearRecords(this.medicalMemberNumber);
        clearRecords(this.pharmacyMemberNumber);
        clearRecords(this.expirationDate);
        clearRecords(this.selectedPrescriber);
        clearRecords(this.selectedPractice);
        this.isReenrollment = false;
        this.reenrollmentPages = [];
        clearRecords(this.reenrollPatient);
    }

    @Mutation
    setExpirationDate(expirationDate: Moment) {
        this.expirationDate = expirationDate;
    }

    @Mutation
    setMedicalMemberNumber(memberNumber: string | null) {
        this.medicalMemberNumber = memberNumber;
    }

    @Mutation
    setPharmacyMemberNumber(memberNumber: string | null) {
        this.pharmacyMemberNumber = memberNumber;
    }

    @Action({ rawError: true })
    async submitToAPI() {
        let response = null;
        try {
            const patient = {
                account: {
                    sourceId: parseInt(this.settings.sourceId),
                    patientData: {
                        canUpdate: true,
                        canInsert: true,
                        skipSearch: false,
                        model: {
                            ...this.patientInformationData,
                            middleName: '',
                            suffix: '',
                            nickname: '',
                        } as EnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
                    } as UpsearchModelEnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
                    accountData: {
                        canUpdate: true,
                        canInsert: true,
                        skipSearch: false,
                        model: {
                            accountType: 1,
                            accountStatus: 1,
                            contactPreferences: this.contactPreferences,
                            contacts: [] as EnterpriseCoordinatorAccountServiceModelsAccountContactUpsearchModel[],
                            externalIds: this.session.isHub
                                ? [
                                      {
                                          externalIdType: enums.AccountExternalIdType.THIRDPARTYID,
                                          externalId: this.thirdPartyId,
                                      },
                                  ]
                                : this.session['https://apigateway.trialcard.com/account'] && [
                                      {
                                          externalIdType: enums.AccountExternalIdType.SiteAccountId,
                                          externalId: this.session['https://apigateway.trialcard.com/account'],
                                      },
                                  ],
                        } as any,
                    } as UpsearchModelEnterpriseCoordinatorAccountServiceModelsAccountUpsearchModel,
                    payorData: this.prepPayorModel(),
                    prescriberData: [
                        {
                            canUpdate: true,
                            canInsert: true,
                            skipSearch: this.session.isHub,
                            model: {
                                ...this.selectedPrescriber,
                                accountRelationshipIsPrimary: true,
                            },
                        },
                    ],
                    siteData: this.prepSiteModel(),
                    surveySessionData: [this.survey.sessionId] as number[],
                } as EnterpriseCoordinatorAccountUpsearchRequestModel,
                caseInitiator: enums.EnrollPatientCaseInitiator.provider,
            } as EnrollPatientRequestModel;
            response = await this.patientEnrollmentApi.patientEnrollmentEnrollPatient(patient);
            const enrollment = response.data && response.data.data;
            if (enrollment) {
                const memberNumbers = enrollment.enrollment && enrollment.enrollment.memberNumbers;
                const medicalMemberNumber = memberNumbers && memberNumbers.find(x => x.type === enums.EnrollmentType.Medical);
                const pharmacyMemberNumber = memberNumbers && memberNumbers.find(x => x.type === enums.EnrollmentType.Pharmacy);
                if (medicalMemberNumber) this.setMedicalMemberNumber(medicalMemberNumber.number!);
                if (pharmacyMemberNumber) this.setPharmacyMemberNumber(pharmacyMemberNumber.number!);
                const enrollmentExpirationDate = enrollment.enrollment && enrollment.enrollment.endDate;
                if (enrollmentExpirationDate) this.setExpirationDate(enrollmentExpirationDate);
            }
        } catch (error) {
            const test = error;
            response = { status: 400 } as AxiosResponse;
        }
        return response;
    }

    prepSiteModel() {
        const response: UpsearchModelEnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel[] = [
            {
                canUpdate: true,
                canInsert: true,
                skipSearch: !!this.session.isHub,
                model: {
                    ...this.selectedPractice,
                    accountRelationshipIsPrimary: true,
                    pointOfContacts: [],
                } as EnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel,
            },
        ];
        _.map(response, site => {
            if (site.model!.phoneNumbers && site.model!.phoneNumbers.length > 0) {
                const phone = site.model!.phoneNumbers.find(x => x.phoneType === enums.ContactInfoType.PHONE) || {};
                const fax = site.model!.phoneNumbers.find(x => x.phoneType === enums.ContactInfoType.FAX) || {};
                // eslint-disable-next-line no-unused-expressions
                if (phone || fax) {
                    // eslint-disable-next-line no-unused-expressions
                    (((site || {}).model || {}).pointOfContacts || []).push({
                        phoneNumber: phone!.phoneNumber,
                        faxNumber: fax!.phoneNumber,
                        preferredContactMethod: fax ? enums.ContactInfoType.FAX : enums.ContactInfoType.PHONE,
                    });
                }
            }
        });
        return response;
    }

    prepPayorModel() {
        const response: UpsearchModelEnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel[] = [
            {
                canUpdate: true,
                canInsert: true,
                skipSearch: false,
                model: {
                    accountRoleType: enums.RoleType.InsuranceProvider,
                    accountRelationshipCoverageOrder: 1,
                    accountRelationshipIsPrimary: true,
                    payorName: this.primaryPayerInformationData.name,
                    payorType: enums.PayorType.INS,
                    payorBenefit: {
                        phone: this.primaryPayerInformationData.phone.replace(/\D/g, ''),
                        groupNumber: this.primaryPayerInformationData.groupNumber,
                    } as EnterpriseCoordinatorPayorServiceModelsPayorBenefitModel,
                } as EnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel,
            },
        ];
        response.push({
            canUpdate: true,
            canInsert: true,
            skipSearch: false,
            model: {
                accountRoleType: enums.RoleType.PharmacyBenefitManager,
                accountRelationshipCoverageOrder: 1,
                accountRelationshipIsPrimary: true,
                payorName: this.primaryPrescriptionInformationData.name,
                payorType: enums.PayorType.PBM,
                payorBenefit: {
                    groupNumber: this.primaryPrescriptionInformationData.groupNumber,
                    bin: this.primaryPrescriptionInformationData.bin,
                    pcn: this.primaryPrescriptionInformationData.pcn,
                    phone: this.primaryPrescriptionInformationData.phone
                        ? this.primaryPrescriptionInformationData.phone.replace(/\D/g, '')
                        : undefined,
                    policyNumber: this.primaryPrescriptionInformationData.policyHolderId,
                },
            } as EnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel,
        });
        if (
            this.secondaryPayerInformationData.name ||
            this.secondaryPayerInformationData.groupNumber ||
            this.secondaryPayerInformationData.phone
        ) {
            response.push({
                canUpdate: true,
                canInsert: true,
                skipSearch: false,
                model: {
                    accountRoleType: enums.RoleType.InsuranceProvider,
                    accountRelationshipCoverageOrder: 2,
                    accountRelationshipIsPrimary: false,
                    payorName: this.secondaryPayerInformationData.name,
                    payorType: enums.PayorType.INS,
                    payorBenefit: {
                        phone: this.secondaryPayerInformationData.phone
                            ? this.secondaryPayerInformationData.phone.replace(/\D/g, '')
                            : undefined,
                        groupNumber: this.secondaryPayerInformationData.groupNumber,
                    },
                } as EnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel,
            });
        }
        if (
            this.secondaryPrescriptionInformationData.name ||
            this.secondaryPrescriptionInformationData.bin ||
            this.secondaryPrescriptionInformationData.groupNumber ||
            this.secondaryPrescriptionInformationData.pcn ||
            this.secondaryPrescriptionInformationData.phone ||
            this.secondaryPrescriptionInformationData.policyHolderId
        ) {
            response.push({
                canUpdate: true,
                canInsert: true,
                skipSearch: false,
                model: {
                    accountRoleType: enums.RoleType.PharmacyBenefitManager,
                    accountRelationshipCoverageOrder: 2,
                    accountRelationshipIsPrimary: false,
                    payorName: this.secondaryPrescriptionInformationData.name,
                    payorType: enums.PayorType.PBM,
                    payorBenefit: {
                        groupNumber: this.secondaryPrescriptionInformationData.groupNumber,
                        bin: this.secondaryPrescriptionInformationData.bin,
                        pcn: this.secondaryPrescriptionInformationData.pcn,
                        phone: this.secondaryPrescriptionInformationData.phone
                            ? this.secondaryPrescriptionInformationData.phone.replace(/\D/g, '')
                            : undefined,
                        policyNumber: this.secondaryPrescriptionInformationData.policyHolderId,
                    },
                } as EnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel,
            });
        }
        return response;
    }
}

interface IPatientInformation {
    firstName?: string;
    lastName?: string;
    dateOfBirth?: Moment | null;
    addresses?: EnterpriseCoordinatorSharedAddressUpsearchModel[];
    emailAddresses?: EnterpriseCoordinatorSharedEmailAddressUpsearchModel[];
    phoneNumbers?: EnterpriseCoordinatorSharedPhoneUpsearchModel[];
    gender?: enums.Gender | null;
}
